import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { leaveApi } from '@/lib/api';
import { Leave, LeaveApplicationRequest } from '@/types';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { formatDate } from '@/lib/utils';
import { 
  PlusIcon, 
  CheckIcon, 
  XMarkIcon, 
  ClockIcon,
  CalendarDaysIcon,
  DocumentTextIcon,
  UserIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

export default function LeavesPage() {
  const { user } = useAuth();
  const [myLeaves, setMyLeaves] = useState<Leave[]>([]);
  const [pendingLeaves, setPendingLeaves] = useState<Leave[]>([]);
  const [loading, setLoading] = useState(true);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [showApplyForm, setShowApplyForm] = useState(false);
  const [activeTab, setActiveTab] = useState<'my-leaves' | 'pending'>('my-leaves');
  const [formData, setFormData] = useState<LeaveApplicationRequest>({
    leaveType: 'casual',
    startDate: '',
    endDate: '',
    reason: '',
    isHalfDay: false,
    halfDayPeriod: undefined,
  });
  const [submitting, setSubmitting] = useState(false);
  const [page, setPage] = useState(1);
  const [pendingPage, setPendingPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pendingTotalPages, setPendingTotalPages] = useState(1);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const isAdminOrHR = user?.role === 'ADMIN' || user?.role === 'HR'||user?.role==='SUPERADMIN' || user?.role === 'MANAGER';
  const isSUPERADMIN=user?.role==='SUPERADMIN'
  const isManager = user?.role === 'MANAGER';

  useEffect(() => {
    fetchMyLeaves();
    if (isAdminOrHR) {
      fetchPendingLeaves();
    }
  }, [page, pendingPage]);

  const fetchMyLeaves = async () => {
    try {
      setLoading(true);
      const response = await leaveApi.getMyLeaves({ page, limit: 10 });
      if (response.success && response.data) {
        setMyLeaves(response.data);
        if (response.pagination) {
          setTotalPages(response.pagination.pages);
        }
      }
    } catch (error) {
      console.error('Error fetching my leaves:', error);
      alert('Failed to fetch leave records');
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingLeaves = async () => {
    if (!isAdminOrHR) return;
    console.log("Fetching pending leaves for SUPERADMIN...",isAdminOrHR);
    try {
      setPendingLoading(true);
      const response = await leaveApi.getPendingLeaves({ page: pendingPage, limit: 10 });
      if (response.success && response.data) {
        // Filter leaves for manager - only show leaves from their department
        let filteredLeaves = response.data;
        if (isManager && user?.department) {
          filteredLeaves = response.data.filter(
            (leave: Leave) => leave.employee?.department === user.department
          );
        }
        setPendingLeaves(filteredLeaves);
        if (response.pagination) {
          setPendingTotalPages(response.pagination.pages);
        }
      }
    } catch (error) {
      console.error('Error fetching pending leaves:', error);
      alert('Failed to fetch pending leaves');
    } finally {
      setPendingLoading(false);
    }
  };

  const handleApplyLeave = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (new Date(formData.startDate) > new Date(formData.endDate)) {
      alert('End date cannot be before start date');
      return;
    }

    if (formData.isHalfDay && !formData.halfDayPeriod) {
      alert('Please select half day period');
      return;
    }

    setSubmitting(true);

    try {
      const response = await leaveApi.apply({
        ...formData,
        halfDayPeriod: formData.isHalfDay ? formData.halfDayPeriod : undefined,
      });
      
      if (response.success) {
        setShowApplyForm(false);
        setFormData({
          leaveType: 'casual',
          startDate: '',
          endDate: '',
          reason: '',
          isHalfDay: false,
          halfDayPeriod: undefined,
        });
        fetchMyLeaves();
        alert('Leave application submitted successfully!');
      }
    } catch (error: any) {
      console.error('Error applying leave:', error);
      const errorMessage = error.response?.data?.error || 'Failed to submit leave application';
      alert(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleApproveLeave = async (leaveId: string) => {
    if (!window.confirm('Are you sure you want to approve this leave application?')) {
      return;
    }

    setActionLoading(leaveId);
    try {
      const response = await leaveApi.approve(leaveId);
      if (response.success) {
        fetchPendingLeaves();
        alert('Leave application approved successfully!');
      }
    } catch (error: any) {
      console.error('Error approving leave:', error);
      const errorMessage = error.response?.data?.error || 'Failed to approve leave';
      alert(errorMessage);
    } finally {
      setActionLoading(null);
    }
  };

  const handleRejectLeave = async (leaveId: string) => {
    const rejectionReason = prompt('Please enter the reason for rejection:');
    if (!rejectionReason || rejectionReason.trim().length === 0) {
      alert('Rejection reason is required');
      return;
    }

    setActionLoading(leaveId);
    try {
      const response = await leaveApi.reject(leaveId, rejectionReason.trim());
      if (response.success) {
        fetchPendingLeaves();
        alert('Leave application rejected');
      }
    } catch (error: any) {
      console.error('Error rejecting leave:', error);
      const errorMessage = error.response?.data?.error || 'Failed to reject leave';
      alert(errorMessage);
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusClasses = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      approved: 'bg-green-100 text-green-800 border-green-200',
      rejected: 'bg-red-100 text-red-800 border-red-200',
    };

    const statusClass = statusClasses[status as keyof typeof statusClasses] || 'bg-gray-100 text-gray-800 border-gray-200';

    return (
      <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium border ${statusClass}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const calculateLeaveDays = (startDate: string, endDate: string, isHalfDay: boolean) => {
    if (!startDate || !endDate) return 0;
    
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    
    return isHalfDay ? 0.5 : diffDays;
  };

  const leaveTypes = [
    { value: 'sick', label: 'Sick Leave', icon: 'ðŸ¤’' },
    { value: 'casual', label: 'Casual Leave', icon: 'ðŸ˜Š' },
    { value: 'annual', label: 'Annual Leave', icon: 'ðŸ–ï¸' },
    { value: 'maternity', label: 'Maternity Leave', icon: 'ðŸ‘¶' },
    { value: 'paternity', label: 'Paternity Leave', icon: 'ðŸ‘¨â€ðŸ‘¶' },
    { value: 'emergency', label: 'Emergency Leave', icon: 'ðŸš¨' },
    { value: 'unpaid', label: 'Unpaid Leave', icon: 'ðŸ’¸' },
  ];

  const renderLeaveCard = (leave: Leave, showEmployeeInfo = false) => {
    console.log("Rendering leave card for leave:",leave);
    return(
    <Card key={leave._id} className="hover:shadow-md transition-shadow">
      <CardContent className="p-6">
        <div className="flex justify-between items-start mb-4">
          <div className="flex items-center space-x-3">
            {/* <div className="text-2xl">
              {leaveTypes.find(type => type.value === leave.leaveType)?.icon || 'ðŸ“‹'}
            </div> */}
            
            <div>
              <h3 className="text-lg font-semibold text-gray-900 capitalize">
                {leave.leaveType.replace('_', ' ')} Leave
              </h3>
              {showEmployeeInfo && leave.employee && (
                <p className="text-sm text-gray-600 flex items-center">
                  <UserIcon className="h-4 w-4 mr-1" />
                  {leave.employee.firstName} {leave.employee.lastName} ({leave.employee.employeeId})
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {getStatusBadge(leave.status)}
            {leave.isHalfDay && (
              <span className="inline-flex items-center rounded-full px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
                Half Day ({leave.halfDayPeriod})
              </span>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
          <div className="flex items-center text-sm">
            <CalendarDaysIcon className="h-4 w-4 mr-2 text-gray-500" />
            <div>
              <span className="font-medium text-gray-700">Duration:</span>
              <p className="text-gray-600">
                {formatDate(leave.startDate)} to {formatDate(leave.endDate)}
              </p>
              <p className="text-xs text-gray-500">
                ({calculateLeaveDays(leave.startDate.toString(), leave.endDate.toString(), leave.isHalfDay)} days)
              </p>
            </div>
          </div>
          
          <div className="flex items-center text-sm">
            <ClockIcon className="h-4 w-4 mr-2 text-gray-500" />
            <div>
              <span className="font-medium text-gray-700">Applied on:</span>
              <p className="text-gray-600">{formatDate(leave.appliedDate)}</p>
            </div>
          </div>
          
          {leave.approvedDate && (
            <div className="flex items-center text-sm">
              <CheckIcon className="h-4 w-4 mr-2 text-gray-500" />
              <div>
                <span className="font-medium text-gray-700">
                  {leave.status === 'approved' ? 'Approved on:' : 'Processed on:'}
                </span>
                <p className="text-gray-600">{formatDate(leave.approvedDate)}</p>
              </div>
            </div>
          )}
        </div>

        <div className="mb-4">
          <div className="flex items-start">
            <DocumentTextIcon className="h-4 w-4 mr-2 text-gray-500 mt-0.5" />
            <div>
              <span className="font-medium text-gray-700">Reason:</span>
              <p className="text-gray-600 mt-1">{leave.reason}</p>
            </div>
          </div>
        </div>

        {leave.rejectionReason && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <div className="flex items-start">
              <ExclamationTriangleIcon className="h-4 w-4 mr-2 text-red-500 mt-0.5" />
              <div>
                <span className="font-medium text-red-700">Rejection Reason:</span>
                <p className="text-red-600 mt-1">{leave.rejectionReason}</p>
              </div>
            </div>
          </div>
        )}

        {(isSUPERADMIN || isManager) && leave.status === 'pending' && (
          <div className="flex justify-end space-x-2 pt-4 border-t">
            <Button
              size="sm"
              onClick={() => handleApproveLeave(leave._id)}
              disabled={actionLoading === leave._id}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              {actionLoading === leave._id ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                <>
                  <CheckIcon className="h-4 w-4 mr-1" />
                  Approve
                </>
              )}
            </Button>
            <Button
              size="sm"
              variant="destructive"
              onClick={() => handleRejectLeave(leave._id)}
              disabled={actionLoading === leave._id}
            >
              {actionLoading === leave._id ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                <>
                  <XMarkIcon className="h-4 w-4 mr-1" />
                  Reject
                </>
              )}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );}

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Leave Management</h1>
          <p className="text-gray-600">
            {isManager
              ? `Manage leave applications for your department (${user?.department})`
              : isAdminOrHR 
                ? 'Manage leave applications for all employees'
                : 'Apply for leave and view your leave history'
            }
          </p>
        </div>
        <Button onClick={() => setShowApplyForm(true)} className="bg-blue-600 hover:bg-blue-700">
          <PlusIcon className="mr-2 h-4 w-4" />
          Apply for Leave
        </Button>
      </div>

      {/* Tabs for Admin/HR */}
      {isAdminOrHR && (
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('my-leaves')}
              className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'my-leaves'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              My Leaves
            </button>
            <button
              onClick={() => setActiveTab('pending')}
              className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'pending'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Pending Approvals ({pendingLeaves.length})
            </button>
          </nav>
        </div>
      )}

      {/* Apply Leave Form */}
      {showApplyForm && (
        <Card>
          <CardHeader>
            <CardTitle>Apply for Leave</CardTitle>
            <CardDescription>
              Fill in the details for your leave application. All fields are required.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleApplyLeave} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="leaveType" className="block text-sm font-medium text-gray-700 mb-2">
                    Leave Type *
                  </label>
                  <select
                    id="leaveType"
                    value={formData.leaveType}
                    onChange={(e) => setFormData({ ...formData, leaveType: e.target.value as any })}
                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    {leaveTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                       {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Half Day Leave
                  </label>
                  <div className="flex items-center space-x-4">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.isHalfDay}
                        onChange={(e) => setFormData({ 
                          ...formData, 
                          isHalfDay: e.target.checked,
                          halfDayPeriod: e.target.checked ? 'morning' : undefined
                        })}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Half Day</span>
                    </label>
                    {formData.isHalfDay && (
                      <select
                        value={formData.halfDayPeriod}
                        onChange={(e) => setFormData({ ...formData, halfDayPeriod: e.target.value as any })}
                        className="rounded-md border border-gray-300 bg-white px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      >
                        <option value="morning">Morning</option>
                        <option value="afternoon">Afternoon</option>
                      </select>
                    )}
                  </div>
                </div>

                <div>
                  <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-2">
                    Start Date *
                  </label>
                  <Input
                    id="startDate"
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                    min={new Date().toISOString().split('T')[0]}
                    required
                  />
                </div>

                <div>
                  <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-2">
                    End Date *
                  </label>
                  <Input
                    id="endDate"
                    type="date"
                    value={formData.endDate}
                    onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                    min={formData.startDate || new Date().toISOString().split('T')[0]}
                    required
                  />
                </div>
              </div>

              <div>
                <label htmlFor="reason" className="block text-sm font-medium text-gray-700 mb-2">
                  Reason for Leave *
                </label>
                <textarea
                  id="reason"
                  rows={4}
                  value={formData.reason}
                  onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Please provide a detailed reason for your leave request (max 1000 characters)"
                  maxLength={1000}
                  required
                />
                <p className="mt-1 text-xs text-gray-500">
                  {formData.reason.length}/1000 characters
                </p>
              </div>

              {formData.startDate && formData.endDate && (
                <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                  <p className="text-sm font-medium text-blue-900">
                    Leave Duration: {calculateLeaveDays(formData.startDate, formData.endDate, formData.isHalfDay)} days
                  </p>
                </div>
              )}

              <div className="flex justify-end space-x-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowApplyForm(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting}>
                  {submitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Submitting...
                    </>
                  ) : (
                    'Submit Application'
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Content based on active tab */}
      {(!isAdminOrHR || activeTab === 'my-leaves') && (
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">My Leave Applications</h2>
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : myLeaves.length > 0 ? (
            <div className="space-y-4">
              {myLeaves.map((leave) => renderLeaveCard(leave, false))}
              
              {/* My Leaves Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center items-center space-x-2 mt-6">
                  <Button
                    variant="outline"
                    onClick={() => setPage(page - 1)}
                    disabled={page <= 1}
                  >
                    Previous
                  </Button>
                  <span className="px-4 py-2 text-sm text-gray-700">
                    Page {page} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    onClick={() => setPage(page + 1)}
                    disabled={page >= totalPages}
                  >
                    Next
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <CalendarDaysIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No leave applications</h3>
              <p className="mt-1 text-sm text-gray-500">
                You haven't applied for any leaves yet.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Pending Approvals for Admin/HR */}
      {isAdminOrHR && activeTab === 'pending' && (
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Pending Leave Approvals</h2>
          {pendingLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : pendingLeaves.length > 0 ? (
            <div className="space-y-4">
              {pendingLeaves.map((leave) => renderLeaveCard(leave, true))}
              
              {/* Pending Leaves Pagination */}
              {pendingTotalPages > 1 && (
                <div className="flex justify-center items-center space-x-2 mt-6">
                  <Button
                    variant="outline"
                    onClick={() => setPendingPage(pendingPage - 1)}
                    disabled={pendingPage <= 1}
                  >
                    Previous
                  </Button>
                  <span className="px-4 py-2 text-sm text-gray-700">
                    Page {pendingPage} of {pendingTotalPages}
                  </span>
                  <Button
                    variant="outline"
                    onClick={() => setPendingPage(pendingPage + 1)}
                    disabled={pendingPage >= pendingTotalPages}
                  >
                    Next
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <ClockIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No pending approvals</h3>
              <p className="mt-1 text-sm text-gray-500">
                All leave applications have been processed.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
